import * as StoreTypes from "../DataTypes/storeTypes";
import * as ActionGenerics from "../../Common/actionGenerics";
import * as ActionTypes from "../Actions/actionTypes";
import * as ServiceTypes from "../DataTypes/serviceTypes";
import * as ProviderTypes from "../DataTypes/providerTypes";
import * as LocationTypes from "../DataTypes/locationTypes";
import * as DateTypes from "../DataTypes/dateTypes";
import * as moment from "moment";
import * as CalendarHelper from "../Helpers/calendarHelper";
import * as StripeTypes from "../../Common/Stripe/types";
import * as ClientTypes from "../DataTypes/clientTypes";
import * as bookingHelper from "../Helpers/bookingHelper";
import { SlotType } from "../DataTypes/dateTypes";
import { IStripeFeeData } from "../DataTypes/serviceTypes";

export const stepsInfo = (
    state: StoreTypes.IStepsInfo,
    action: ActionGenerics.IAction<ActionTypes.Actions>,
    initialSettings: StoreTypes.IInitialSettings,
    servicesData: ServiceTypes.IServicesData,
    locations: LocationTypes.ILocation[],
    providers: ProviderTypes.IProvider[],
    clients: ClientTypes.IClient[]
): StoreTypes.IStepsInfo => {
    switch (action.type) {
        case ActionTypes.Actions.SelectLocation: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, number>;

            const location = locations.find((l) => l.id === data.parameter);

            const stepInfo: StoreTypes.IStepInfo = {
                id: location.id,
                summary: location.name,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Location] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.SelectService: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, number>;

            const service = servicesData.services.find((l) => l.id === data.parameter);

            const currentStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const stepInfo: StoreTypes.IServiceStepInfo = {
                id: service.id,
                summary: service.name,
                amount: service.amount,
                totalAmount: service.amount,
                selectedCategory: currentStepInfo.selectedCategory,
                hasPortalDeposit: service.portalDeposit > 0,
                isPortalDeposit: false,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.SelectServiceCategory: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, string>;

            const currentStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const stepInfo: StoreTypes.IServiceStepInfo = { ...currentStepInfo, selectedCategory: data.parameter };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.SelectProvider: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, number>;

            const provider = providers.find((l) => l.id === data.parameter);

            const stepInfo: StoreTypes.IStepInfo = {
                id: provider.id,
                summary: provider.name,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Provider] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.SelectDate: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, moment.Moment>;

            const currentStepInfo = state[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;
            const day = data.parameter;
            const date = day ? day.clone() : null;

            const stepInfo: StoreTypes.IAvailabilityStepInfo = {
                ...currentStepInfo,
                selectedDate: date,
                slot: null,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.When] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.SelectSlot: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, DateTypes.ISlot>;

            const currentStepInfo = state[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;

            const providerStepInfo = state[StoreTypes.Step.Provider];

            const stepInfo: StoreTypes.IAvailabilityStepInfo = {
                ...currentStepInfo,
                slot: data.parameter,
                summary: CalendarHelper.getAppointmentDateTimeLabel(data.parameter.date),
                id: providerStepInfo.id > 0 ? 1 : null, //put fake number for navigation purposes
            };

            const newState = { ...state };

            newState[StoreTypes.Step.When] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.NavigateMonthBefore: {
            const currentStepInfo = state[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;

            const date = CalendarHelper.getShowingDateByInfo(currentStepInfo);

            const monthAgo = date.startOf("month").subtract(1, "month");

            const stepInfo: StoreTypes.IAvailabilityStepInfo = {
                ...currentStepInfo,
                navigationDate: monthAgo,
                selectedDate: null,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.When] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.NavigateMonthNext: {
            const currentStepInfo = state[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;

            const date = CalendarHelper.getShowingDateByInfo(currentStepInfo);

            const monthAgo = date.startOf("month").add(1, "month");

            const stepInfo: StoreTypes.IAvailabilityStepInfo = {
                ...currentStepInfo,
                navigationDate: monthAgo,
                selectedDate: null,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.When] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.UpdateShowNotes: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, boolean>;

            const currentStepInfo = state[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;

            const stepInfo: StoreTypes.IConfirmationStepInfo = {
                ...currentStepInfo,
                showAddNotes: data.parameter,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Confirmation] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.UpdateShowStripe: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, boolean>;

            const currentStepInfo = state[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;

            const stepInfo: StoreTypes.IConfirmationStepInfo = {
                ...currentStepInfo,
                showStripe: data.parameter,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Confirmation] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.SetIdempotencyKey: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, string>;

            const currentStepInfo = state[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;

            const stepInfo: StoreTypes.IConfirmationStepInfo = {
                ...currentStepInfo,
                idempotencyKey: data.parameter,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Confirmation] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.UpdateCreditCard: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, StripeTypes.ICardInfo>;

            const currentStepInfo = state[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;

            const stepInfo: StoreTypes.IConfirmationStepInfo = {
                ...currentStepInfo,
                cardInfo: data.parameter,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Confirmation] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.EditCreditCard: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, boolean>;

            const currentStepInfo = state[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;

            const stepInfo: StoreTypes.IConfirmationStepInfo = {
                ...currentStepInfo,
                isEditCreditCard: data.parameter,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Confirmation] = stepInfo;

            return newState;
        }

        case ActionTypes.Actions.SelectClient: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, number>;

            const currentStepInfo = state[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;

            const stepInfo: StoreTypes.IConfirmationStepInfo = {
                ...currentStepInfo,
                clientId: data.parameter,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Confirmation] = stepInfo;

            return newState;
        }
        case ActionTypes.Actions.ApplyClientDiscount: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, number>;

            const serviceStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const service = servicesData.services.find((l) => l.id === serviceStepInfo.id);
            const currentClient = clients.find((x) => x.id === data.parameter);

            // Required to check for undefined 'currentClient' due to cases where a contact could be logged in which does not have 'Customer' permissions and user needs to select manually the client they want to book an appointment for.
            const amount = bookingHelper.applyDiscount(
                service?.amount,
                currentClient?.discount,
                currentClient?.discountType
            );

            const serviceInfo: StoreTypes.IServiceStepInfo = {
                ...serviceStepInfo,
                amount: amount,
                totalAmount: amount,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = serviceInfo;

            return newState;
        }

        case ActionTypes.Actions.ApplyDeposit: {
            const serviceStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;
            const service = servicesData.services.find((l) => l.id === serviceStepInfo.id);

            const serviceInfo: StoreTypes.IServiceStepInfo = {
                ...serviceStepInfo,
                amount:
                    !!service.portalDeposit && service.portalDeposit < serviceStepInfo.amount
                        ? service.portalDeposit
                        : serviceStepInfo.amount,
                isPortalDeposit: true,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = serviceInfo;

            return newState;
        }

        case ActionTypes.Actions.ApplyFullPayment: {
            const serviceStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const serviceInfo: StoreTypes.IServiceStepInfo = {
                ...serviceStepInfo,
                amount: serviceStepInfo.totalAmount,
                amountFee: serviceStepInfo.totalAmountFee,
                displayAmount: serviceStepInfo.displayTotalAmount,
                isPortalDeposit: false,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = serviceInfo;

            return newState;
        }

        case ActionTypes.Actions.ApplyTax: {
            const serviceStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const service = servicesData.services.find((l) => l.id === serviceStepInfo.id);

            const serviceInfo: StoreTypes.IServiceStepInfo = {
                ...serviceStepInfo,
                amount: bookingHelper.applyTax(
                    initialSettings.accountInitialSettings.organisationInfo.taxUsage,
                    serviceStepInfo.amount,
                    service?.taxRate
                ),
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = serviceInfo;

            return newState;
        }
        case ActionTypes.Actions.FlushSteps: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, StoreTypes.Step[]>;
            const steps = data.parameter;

            steps.forEach((step) => ((state[step] as any) = {}));

            if (steps.includes(StoreTypes.Step.When)) {
                const availabilityStep = state[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;
                availabilityStep.start = moment().startOf("month");
            }

            return { ...state };
        }

        case ActionTypes.Actions.SelectGroup: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, DateTypes.ISlot>;

            const groupStepInfo: StoreTypes.IStepInfo = {
                summary: data.parameter && data.parameter.type === SlotType.Class ? data.parameter.name : "",
                id: null,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Group] = groupStepInfo;

            return newState;
        }

        case ActionTypes.Actions.UpdateService: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, ServiceTypes.IService>;
            const service = data.parameter;

            const serviceStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const serviceInfo: StoreTypes.IServiceStepInfo = {
                ...serviceStepInfo,
                id: service?.id,
                summary: service?.name,
                amount: service?.amount,
                totalAmount: service?.amount,
                hasPortalDeposit: service?.portalDeposit > 0,
                isPortalDeposit: false,
            };

            const newState = { ...state };
            newState[StoreTypes.Step.Service] = serviceInfo;

            return newState;
        }

        case ActionTypes.Actions.ApplyAmountStripeFee: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, IStripeFeeData>;
            const serviceStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const serviceInfo: StoreTypes.IServiceStepInfo = {
                ...serviceStepInfo,
                displayAmount: data.parameter.totalAmount,
                amountFee: data.parameter.totalFee,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = serviceInfo;

            return newState;
        }

        case ActionTypes.Actions.ApplyTotalAmountStripeFee: {
            const data = action as ActionGenerics.IParamAction<ActionTypes.Actions, IStripeFeeData>;
            const serviceStepInfo = state[StoreTypes.Step.Service] as StoreTypes.IServiceStepInfo;

            const serviceInfo: StoreTypes.IServiceStepInfo = {
                ...serviceStepInfo,
                displayTotalAmount: data.parameter.totalAmount,
                totalAmountFee: data.parameter.totalFee,
            };

            const newState = { ...state };

            newState[StoreTypes.Step.Service] = serviceInfo;

            return newState;
        }

        default:
            return state;
    }
};
